import React, { useEffect, useState } from "react";
import { apiFetch } from "../../../../apiFetch";
import { getFlagCode } from "../../../../utils/countryFlags";
import "./Leaderboard.css";

const Leaderboard = () => {
    const [players, setPlayers] = useState([]);
    const [yourRank, setYourRank] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // 🔹 NEW: gender from /me
    const [gender, setGender] = useState(null);

    // 🔹 Date defaults
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    // 🔹 Filters
    const [year, setYear] = useState(currentYear);
    const [mode, setMode] = useState("yearly");
    const [month, setMonth] = useState(currentMonth);
    const [position, setPosition] = useState(null);
    const [limit, setLimit] = useState(5);

    // 🔹 Player context
    const currentPlayerId = localStorage.getItem("playerId");

    // 🔹 NEW: load player gender from /me
    useEffect(() => {
        const loadMe = async () => {
            try {
                const res = await apiFetch("/me");

                if (!res.ok) return;

                const data = await res.json();

                if (data?.gender) {
                    setGender(String(data.gender).toLowerCase());
                }
            } catch (err) {
                console.error("Failed to load /me:", err);
            }
        };

        loadMe();
    }, []);

    useEffect(() => {
        const fetchLeaderboard = async () => {
            setLoading(true);
            setError(null);

            try {
                const endpoint =
                    mode === "monthly"
                        ? "/api/leaderboard/monthly"
                        : "/api/leaderboard";

                const params = new URLSearchParams({
                    year: String(year),
                    limit: String(limit),
                    playerId: String(currentPlayerId),
                });

                // 🔹 send gender only if known
                if (gender) {
                    params.set("gender", String(gender));
                }

                if (mode === "monthly") {
                    params.set("month", String(month));
                }

                if (position) {
                    params.set("position", String(position));
                }

                const res = await apiFetch(`${endpoint}?${params.toString()}`);

                if (!res.ok) {
                    const text = await res.text();
                    throw new Error(`Leaderboard failed: ${res.status} ${text}`);
                }

                const data = await res.json();

                setPlayers(data?.top || []);
                setYourRank(data?.you || null);
            } catch (err) {
                console.error("Player leaderboard fetch failed:", err);
                setError("Failed to load leaderboard");
            } finally {
                setLoading(false);
            }
        };

        if (currentPlayerId) {
            fetchLeaderboard();
        }
    }, [year, mode, month, gender, position, limit, currentPlayerId]);

    return (
        <div className="leaderboard-page">
            {/* Header */}
            <header className="leaderboard-header">
                <h1>Leaderboard</h1>
                <p>Your position among top players this year</p>

                {/* ⭐ YOUR RANK */}
                {yourRank ? (
                    <p className="leaderboard-explainer">
                        Your rank this {mode === "monthly" ? "month" : "year"}:{" "}
                        <strong>#{yourRank.currentRank}</strong>
                    </p>
                ) : (
                    <p className="leaderboard-explainer">
                        You’re not ranked yet — consistent performances unlock ranking.
                    </p>
                )}
            </header>

            {/* Filters */}
            <section className="leaderboard-filters">
                <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                    <option value={currentYear}>{currentYear}</option>
                    <option value={currentYear - 1}>{currentYear - 1}</option>
                </select>

                <select
                    value={mode}
                    onChange={(e) => {
                        const newMode = e.target.value;
                        setMode(newMode);
                        if (newMode === "monthly") setMonth(currentMonth);
                    }}
                >
                    <option value="yearly">Year</option>
                    <option value="monthly">Month</option>
                </select>

                {mode === "monthly" && (
                    <select
                        value={month}
                        onChange={(e) => setMonth(Number(e.target.value))}
                    >
                        {[
                            "January", "February", "March", "April", "May", "June",
                            "July", "August", "September", "October", "November", "December",
                        ].map((m, i) => (
                            <option key={i} value={i + 1}>
                                {m}
                            </option>
                        ))}
                    </select>
                )}

                <select
                    value={position ?? ""}
                    onChange={(e) => setPosition(e.target.value || null)}
                >
                    <option value="">All Positions</option>
                    <option value="ST">ST</option>
                    <option value="CF">CF</option>
                    <option value="LW">LW</option>
                    <option value="RW">RW</option>
                    <option value="AM">AM</option>
                    <option value="CM">CM</option>
                    <option value="CDM">CDM</option>
                    <option value="CB">CB</option>
                    <option value="LB">LB</option>
                    <option value="RB">RB</option>
                    <option value="GK">GK</option>
                </select>

                <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                    <option value={5}>Top 5</option>
                    <option value={10}>Top 10</option>
                </select>
            </section>

            {/* Leaderboard */}
            <section className="leaderboard-shell">
                <section className="leaderboard-list">
                    {loading && (
                        <div className="leaderboard-message">
                            Loading leaderboard…
                        </div>
                    )}

                    {!loading && error && (
                        <div className="leaderboard-error">{error}</div>
                    )}

                    {!loading && !error && players.length === 0 && (
                        <div className="leaderboard-message">
                            No rankings available yet.
                        </div>
                    )}

                    {!loading &&
                        !error &&
                        players.map((player, index) => {
                            const isYou = String(player.playerId) === String(currentPlayerId);
                            const flagCode = getFlagCode(player.nationality);

                            const rawDelta = player.movementDelta;
                            const delta =
                                rawDelta === null || rawDelta === undefined
                                    ? null
                                    : Number(rawDelta);

                            const movementClass =
                                delta > 0 ? "up" : delta < 0 ? "down" : "neutral";

                            return (
                                <div
                                    key={player.playerId}
                                    className={`leaderboard-row ${isYou ? "highlight" : ""}`}
                                >
                                    <div className="rank">#{index + 1}</div>

                                    <div className="player-info">
                                        <div className="player-line">
                                            {flagCode && (
                                                <img
                                                    src={`/flags/${flagCode}.svg`}
                                                    alt={player.nationality}
                                                    className="flag"
                                                />
                                            )}
                                            <span className="player-name">
                                                {isYou ? "You" : player.fullName}
                                            </span>
                                        </div>

                                        <div className="player-meta">
                                            {player.position} · {player.nationality}
                                        </div>
                                    </div>

                                    <div className={`movement ${movementClass}`}>
                                        {delta === null ? (
                                            <span className="delta neutral">NEW</span>
                                        ) : delta === 0 ? (
                                            <span className="delta neutral">—</span>
                                        ) : (
                                            <>
                                                <span className="arrow">
                                                    {delta > 0 ? "↑" : "↓"}
                                                </span>
                                                <span className="delta">
                                                    {delta > 0 ? `+${delta}` : delta}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                </section>
            </section>
        </div>
    );
};

export default Leaderboard;



